import { useEffect, useState } from 'react'
import Topbar from '../components/layout/Topbar'
import Button from '../components/shared/Button'
import Input from '../components/shared/Input'
import { useToast } from '../components/shared/Toast'
import api from '../services/api'
import { useAppStore } from '../store/useAppStore'

interface SettingsForm {
  display_name: string
  email: string
  bio: string
  current_password: string
  new_password: string
  confirm_password: string
  theme: 'light' | 'dark'
  notifications: boolean
  compute_type: 'local' | 'hybrid' | 'private'
}

export default function Settings() {
  const { user } = useAppStore()
  const toast = useToast()
  const [loading, setLoading] = useState(false)
  const [_settingsLoading, setSettingsLoading] = useState(true)

  const [formData, setFormData] = useState<SettingsForm>({
    display_name: user?.display_name ?? '',
    email: user?.email ?? '',
    bio: user?.bio ?? '',
    current_password: '',
    new_password: '',
    confirm_password: '',
    theme: 'dark',
    notifications: true,
    compute_type: 'local',
  })

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const target = e.target as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    const { name, type, value } = target
    const fieldValue = type === 'checkbox' ? (target as HTMLInputElement).checked : value
    setFormData(prev => ({ ...prev, [name]: fieldValue }))
  }

  useEffect(() => {
    api.get('/settings')
      .then(response => {
        const data = response.data as Partial<SettingsForm>
        setFormData(prev => ({
          ...prev,
          theme: data.theme ?? prev.theme,
          notifications: data.notifications ?? prev.notifications,
          compute_type: data.compute_type ?? prev.compute_type,
        }))
      })
      .catch(error => {
        console.warn('Settings load failed:', error)
        toast.error('Ayarlar yuklenemedi')
      })
      .finally(() => setSettingsLoading(false))
  }, [])

  const handleSaveProfile = async () => {
    try {
      setLoading(true)
      await api.put('/api/user/settings', {
        user_id: user?.id,
        display_name: formData.display_name,
        bio: formData.bio,
      })
      toast.success('Profil guncellendi')
    } catch (_error) {
      toast.error('Profil guncellenemedi')
    } finally {
      setLoading(false)
    }
  }

  const handleSaveSettings = async () => {
    try {
      setLoading(true)
      await api.put('/settings', {
        theme: formData.theme,
        notifications: formData.notifications,
        compute_type: formData.compute_type,
      })
      toast.success('Ayarlar kaydedildi')
    } catch (_error) {
      toast.error('Ayarlar kaydedilemedi')
    } finally {
      setLoading(false)
    }
  }

  const handleChangePassword = async () => {
    if (formData.new_password !== formData.confirm_password) {
      toast.error('Sifreler eslesmiyor')
      return
    }
    try {
      setLoading(true)
      await api.post('/api/auth/change-password', {
        user_id: user?.id,
        current_password: formData.current_password,
        new_password: formData.new_password,
      })
      toast.success('Sifre degistirildi')
      setFormData(prev => ({ ...prev, current_password: '', new_password: '', confirm_password: '' }))
    } catch (_error) {
      toast.error('Sifre degistirilemedi')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>      <div style={{ flex: 1, overflow: 'auto' }}>
        <Topbar />
        <div style={{ flex: 1, overflow: 'auto', padding: '2rem' }}>
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <h1>Ayarlar</h1>

            {/* Platform Preferences */}
            <div style={{ marginBottom: '2rem', padding: '1.5rem', background: 'var(--bg-secondary)', borderRadius: '12px' }}>
              <h2 style={{ marginBottom: '1.5rem' }}>Platform Tercihleri</h2>
              <Input
                as="select"
                label="Tema"
                name="theme"
                value={formData.theme}
                onChange={handleChange}
                options={[
                  { value: 'light', label: 'Light' },
                  { value: 'dark', label: 'Dark' },
                ]}
                fullWidth
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 16 }}>
                <input
                  name="notifications"
                  type="checkbox"
                  checked={formData.notifications}
                  onChange={handleChange}
                />
                <span style={{ color: 'var(--text-secondary)' }}>Gelen bildirimleri ac</span>
              </div>
              <Input
                as="select"
                label="Compute Turu"
                name="compute_type"
                value={formData.compute_type}
                onChange={handleChange}
                options={[
                  { value: 'local', label: 'Local' },
                  { value: 'hybrid', label: 'Hybrid' },
                  { value: 'private', label: 'Private' },
                ]}
                fullWidth
              />
              <Button onClick={() => void handleSaveSettings()} loading={loading} variant="primary">
                Ayarlari Kaydet
              </Button>
            </div>

            {/* Profil Ayarlari */}
            <div style={{ marginBottom: '2rem', padding: '1.5rem', background: 'var(--bg-secondary)', borderRadius: '12px' }}>
              <h2 style={{ marginBottom: '1.5rem' }}>Profil Bilgileri</h2>
              <Input
                label="Goruntulenen Ad"
                name="display_name"
                value={formData.display_name}
                onChange={handleChange}
                fullWidth
              />
              <Input
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                fullWidth
              />
              <Input
                as="textarea"
                label="Biyografi"
                name="bio"
                value={formData.bio}
                onChange={handleChange}
                fullWidth
                rows={4}
              />
              <Button onClick={() => void handleSaveProfile()} loading={loading} variant="primary">
                Kaydet
              </Button>
            </div>

            {/* Sifre Degistir */}
            <div style={{ padding: '1.5rem', background: 'var(--bg-secondary)', borderRadius: '12px' }}>
              <h2 style={{ marginBottom: '1.5rem' }}>Sifre Degistir</h2>
              <Input
                label="Mevcut Sifre"
                name="current_password"
                type="password"
                value={formData.current_password}
                onChange={handleChange}
                fullWidth
              />
              <Input
                label="Yeni Sifre"
                name="new_password"
                type="password"
                value={formData.new_password}
                onChange={handleChange}
                fullWidth
              />
              <Input
                label="Yeni Sifre (Tekrar)"
                name="confirm_password"
                type="password"
                value={formData.confirm_password}
                onChange={handleChange}
                fullWidth
              />
              <Button onClick={() => void handleChangePassword()} loading={loading} variant="primary">
                Sifre Degistir
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
